import { createIdMap, NodeIdMap } from '../../../version_mapping/utility'
import { Box, Button, MenuItem, Select } from '@mui/material'
import { getQuestionType } from '../../../utility/TreeHelpers'
import * as React from 'react'
import { INode } from 'shared/types/tree'
import {
  getBadQuestions,
  fixQuestion,
  sortQuestionIssueCounts,
} from './QuestionEvaluation'
import { openNode } from '../../../utility/TreeNavigation'
import { goToBuilder } from '../../TreeActions'
import { KeyPressListener } from '../../../utility/KeyPressListner'
import { IQuestionIssueResolution, QuestionIssues } from './QuestionCleanup'
import { MapIssues } from '../../../QuestionIssues'
import { ConfirmCommitButton } from 'shared/ui'
import { doInBatches } from '../../../../utility'
import { Pagination } from '../../../utility/Pagination'
import { useGetTreeById } from '../../../../reducers/trees_hooks'

export function BadQuestions({
  treeId,
  versionId,
}: {
  treeId: string
  versionId: string
}) {
  const { tree } = useGetTreeById({ treeId, versionId })
  if (!tree) {
    return <Box>Loading...</Box>
  }
  return <BadQuestionsData tree={tree} />
}

function BadQuestionsData({ tree }: { tree: INode }) {
  const [withIssues, setWithIssues] = React.useState<{
    [qid: string]: IQuestionIssueResolution
  }>()
  const [idMap, setIdMap] = React.useState<NodeIdMap>()
  const [lastClicked, setLastClicked] = React.useState('')
  const [useAlt, setUseAlt] = React.useState(false)
  const [filtered, setFiltered] = React.useState<string[]>([])
  const [types, setTypes] = React.useState<string[]>([])
  const [filteredType, setFilteredType] = React.useState<string>('All')
  const [fixed, setFixed] = React.useState<string[]>([])
  const [initiatedFix, setInitiatedFix] = React.useState<string[]>([])
  const [showAllIssues, setShowAllIssues] = React.useState(false)
  const [range, setRange] = React.useState({ start: 0, end: 0 })
  const [issueCount, setIssueCount] = React.useState<{
    [issue: string]: number
  }>({})

  React.useEffect(() => {
    setIdMap(createIdMap(tree))
    setWithIssues(getBadQuestions(tree))
  }, [tree])

  React.useEffect(() => {
    if (!idMap || !withIssues) return
    const { issueCount, questionTypes } = sortQuestionIssueCounts(
      idMap,
      withIssues,
    )
    setIssueCount(issueCount)
    setTypes(questionTypes)
  }, [withIssues, idMap])

  React.useEffect(() => {
    if (!withIssues || !idMap) return
    let filteredSet: string[] = []
    if (filteredType === 'All') {
      filteredSet = Object.keys(withIssues)
    } else if (filteredType === 'UserImpacting') {
      filteredSet = Object.keys(withIssues).filter(
        (key) => withIssues[key].userImpacting,
      )
    } else {
      const f = Object.keys(withIssues).filter((qid) => {
        const question = idMap[qid].node
        return (
          getQuestionType(question) === filteredType &&
          !fixed.includes(question.id)
        )
      })
      setFiltered(f)
      return
    }
    setFiltered(filteredSet.filter((q) => !fixed.includes(q)))
  }, [filteredType, withIssues, fixed, idMap])

  function onQuestionClick(node: INode) {
    if (useAlt) {
      goToBuilder({
        treeId: node.tree_id,
        versionId: node.version,
        nodeId: node.id,
      })
      return
    }
    setLastClicked(node.id)
    openNode(node, idMap!)
  }

  if (!idMap || !withIssues) {
    return <Box>...</Box>
  }

  async function doFixQuestion(
    question: INode,
    fixes: ((q: INode) => Promise<unknown>)[],
  ) {
    setInitiatedFix((prev) => [...prev, question.id])
    await fixQuestion(question, fixes)
    setFixed((prev) => [...prev, question.id])
  }

  function questionFixFunc(questionId: string) {
    if (!withIssues) return
    const fixes = withIssues[questionId].fixes
    if (!fixes.length) return
    return () => {
      if (!idMap) return
      const question = idMap[questionId].node
      return doFixQuestion(question, fixes)
    }
  }

  function fixAll() {
    if (!idMap || !withIssues) return
    const doOne = (qid: string) => {
      const question = idMap[qid].node
      const fixes = withIssues[qid].fixes
      return doFixQuestion(question, fixes)
    }
    return doInBatches(filtered, doOne)
  }

  return (
    <Box>
      <KeyPressListener
        keyPressChange={setUseAlt}
        watchKey={'n'}
      ></KeyPressListener>
      <Box>
        Bad Questions {Object.keys(withIssues).length - fixed.length}{' '}
        <IssueCount issueCount={issueCount} />
        <ConfirmCommitButton onClick={fixAll}>
          <>Fix All</>
        </ConfirmCommitButton>
        <Button onClick={() => setShowAllIssues(!showAllIssues)}>Issues</Button>
      </Box>
      <Box padding={'5px'}>
        <Select
          onChange={(e: any) => setFilteredType(e.target.value)}
          defaultValue="All"
          value={filteredType}
        >
          <MenuItem key={'All'} value={'All'}>
            All Types
          </MenuItem>
          <MenuItem key={'UserImpacting'} value={'UserImpacting'}>
            User Impacting
          </MenuItem>
          {types.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </Select>
        {filtered.length}
      </Box>
      <Pagination total={filtered.length} onRangeChange={setRange} />
      <Box>
        {filtered.slice(range.start, range.end).map((qid) => {
          const border = qid === lastClicked ? 'solid 1px gray' : ''
          return (
            <Box key={qid} border={border} width={'fit-content'}>
              <BadQuestion
                question={idMap[qid].node}
                issues={withIssues[qid]}
                useAlt={useAlt}
                onClick={onQuestionClick}
                questionFix={questionFixFunc(qid)}
                fixDisabled={initiatedFix.includes(qid)}
                initialShowIssues={showAllIssues}
              />
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

function IssueCount({
  issueCount,
}: {
  issueCount: { [issue: string]: number }
}) {
  return (
    <Box
      padding={'3px'}
      width={'fitContent'}
      borderRadius={'3px'}
      fontSize={12}
      color={'#2d2d2d'}
      style={{ backgroundColor: '#FAF9F6' }}
    >
      {Object.entries(issueCount).map(([issue, count]) => {
        return <Box key={issue}>{`${issue}: ${count}`}</Box>
      })}
    </Box>
  )
}

function BadQuestion({
  question,
  useAlt,
  onClick,
  issues,
  questionFix,
  fixDisabled,
  initialShowIssues,
}: {
  question: INode
  useAlt: boolean
  onClick: (node: INode) => void
  issues: IQuestionIssueResolution
  questionFix?: (qid: string) => unknown
  fixDisabled: boolean
  initialShowIssues: boolean
}) {
  const [showIssues, setShowIssues] = React.useState(initialShowIssues)
  const [requiresManualFix, setRequiresManualFix] = React.useState(false)

  React.useEffect(() => {
    setRequiresManualFix(
      issues.requiresId ||
        issues.issues.includes(QuestionIssues.RequiresManualFixing),
    )
  }, [issues])

  React.useEffect(() => {
    setShowIssues(initialShowIssues)
  }, [initialShowIssues])

  function doQuestionFix() {
    if (!questionFix) return
    questionFix(question.id)
  }

  return (
    <Box>
      <Box display="flex" flexDirection="row">
        <Button onClick={() => onClick(question)}>
          {useAlt ? 'Node' : `${getQuestionType(question)}: ${question.title}`}
        </Button>
        <Box fontSize={12}>
          <Button size={'small'} onClick={() => setShowIssues((prev) => !prev)}>
            {issues.issues.join(',')}
          </Button>
          {questionFix && (
            <ConfirmCommitButton disabled={fixDisabled} onClick={doQuestionFix}>
              <>Fix</>
            </ConfirmCommitButton>
          )}
        </Box>
      </Box>
      {showIssues && (
        <Box fontSize={14} paddingLeft={'20px'}>
          {requiresManualFix && <Box>Requires Manual Fix</Box>}
          <MapIssues issueKeys={issues.issues} node={question} />
        </Box>
      )}
    </Box>
  )
}
